import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  CheckCircle2,
  XCircle,
  DollarSign,
  Clock,
  User,
  Wrench
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

export default function PendingApproval() {
  const { unitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPendingTickets();
  }, [unitId]);

  const fetchPendingTickets = async () => {
    try {
      const res = await api.get(`/tickets?unit_id=${unitId}&status=Pending Cost Approval`);
      setTickets(res.data.tickets);
    } catch (error) {
      toast.error("Failed to load pending tickets");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (ticketId) => {
    setSubmitting(true);
    try {
      await api.post(`/tickets/${ticketId}/approve`, { approved: true });
      toast.success("Ticket approved!");
      fetchPendingTickets();
    } catch (error) {
      toast.error("Failed to approve ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTicket || !rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/approve`, {
        approved: false,
        reason: rejectReason
      });
      toast.success("Ticket rejected");
      setRejectDialogOpen(false);
      setRejectReason("");
      fetchPendingTickets();
    } catch (error) {
      toast.error("Failed to reject ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const openRejectDialog = (ticket) => {
    setSelectedTicket(ticket);
    setRejectDialogOpen(true);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={unitId} />
      
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Pending Approvals</h1>
          <p className="text-gray-600 mt-1">Review and approve cost estimates</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : tickets.length > 0 ? (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="bg-white border-gray-200">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Ticket Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 
                          className="text-lg font-semibold text-indigo-600 cursor-pointer hover:underline"
                          onClick={() => navigate(`/ticket/${ticket.id}`)}
                          data-testid={`ticket-link-${ticket.id}`}
                        >
                          {ticket.job_number}
                        </h3>
                        {ticket.priority && (
                          <Badge className={`
                            ${ticket.priority === 'P1' ? 'bg-red-100 text-red-700' :
                              ticket.priority === 'P2' ? 'bg-orange-100 text-orange-700' :
                              ticket.priority === 'P3' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'}
                          `}>
                            {ticket.priority}
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span>{ticket.restaurant_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Wrench className="w-4 h-4" />
                          <span>{ticket.equipment_category}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}</span>
                        </div>
                        {ticket.assignee_name && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <User className="w-4 h-4" />
                            <span>{ticket.assignee_name}</span>
                          </div>
                        )}
                      </div>

                      {ticket.cost_estimate && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-2">{ticket.cost_estimate.diagnosis}</p>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-indigo-600" />
                            <span className="text-2xl font-bold text-gray-900 tabular-nums">
                              ${ticket.cost_estimate.total_cost?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-row lg:flex-col gap-3">
                      <Button
                        onClick={() => handleApprove(ticket.id)}
                        disabled={submitting}
                        className="bg-green-600 hover:bg-green-700 flex-1 lg:flex-none"
                        data-testid={`approve-btn-${ticket.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => openRejectDialog(ticket)}
                        disabled={submitting}
                        variant="destructive"
                        className="flex-1 lg:flex-none"
                        data-testid={`reject-btn-${ticket.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</h3>
              <p className="text-gray-500">No tickets pending your approval</p>
            </CardContent>
          </Card>
        )}

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Cost Estimate</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for rejecting {selectedTicket?.job_number}. 
                This will be shared with the submitter.
              </p>
              <Textarea
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                data-testid="reject-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-reject-btn"
              >
                {submitting ? "Rejecting..." : "Reject"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
